import { useCallback, useContext, useMemo } from 'react'
import { ConfigContext } from '../store/ConfigContext'
import { openSkuSoftware } from '../utils'
import { FeatureFlagKeys } from '../store/Constants'
import DriversAdapter from '../clients/DriversAdapter'
import useLaunchdarkly from './useLaunchdarkly'

/**
 * Easy Start download logic.
 * - If SKU is present: reuse cached 123 software URL for that SKU or fetch again.
 * - If no SKU: fallback to OS-based SWLS flow (Windows/Mac only) via getDriverDownloadUrlByOS.
 */
export default function useEasyStartDownload({
  setLoading = () => {},
  forceByOS = false
} = {}) {
  const { getStoreData, sku, os, flags } = useContext(ConfigContext)

  const { pswApiClientId } = useLaunchdarkly()
  const driversAdapter = useMemo(
    () => new DriversAdapter(undefined, pswApiClientId),
    [pswApiClientId]
  )

  const installEasyStart = useCallback(async () => {
    try {
      setLoading(true)
      if (sku && !forceByOS) {
        const migrationFlag = flags?.[FeatureFlagKeys.migration123]
        const opened = await openSkuSoftware({
          sku,
          getStoreData,
          os,
          flags,
          migrationFlag,
          pswApiClientId
        })
        if (!opened) {
          console.error('EasyStart: Driver URL not found for SKU:', sku)
        }
      } else {
        const driverUrl = await driversAdapter.getDriverDownloadUrlByOS(os)
        if (driverUrl) {
          window.location.href = driverUrl
        }
      }
    } catch (error) {
      console.error('EasyStart: Error fetching drivers:', error)
    } finally {
      setLoading(false)
    }
  }, [sku, os, setLoading, getStoreData, flags, forceByOS])

  return { installEasyStart }
}
